/**
 * Friendly device names for the "Where you're signed in" list.
 *
 * A session's `device` column is what the Settings page shows as the row's
 * label, so it must be something a person recognises — "Chrome on Windows",
 * "Safari on iPhone" — never the raw `Mozilla/5.0 (X11; Linux x86_64) …`
 * fingerprint, which is both unreadable and more than a user needs to see.
 *
 * Deliberately a small hand-rolled matcher rather than a UA-parsing library:
 * the goal is a recognisable browser + platform pair, not device-model
 * forensics, and the ordering below (Edge before Chrome, Chrome before Safari,
 * iPad before Mac) handles the well-known impersonation in UA strings.
 *
 * Mirror of `services/auth/src/http/device-name.ts`. The auth service stores a
 * friendly name at session creation and re-describes legacy rows on the way
 * out, so this client copy is the last line of defence: it guarantees the
 * Settings list never shows a raw UA as a row label even against an older
 * server. Keep the two in step.
 */

export const UNKNOWN_DEVICE = 'Unknown device';

interface Matcher {
  pattern: RegExp;
  label: string;
}

/** Browser matchers — most specific first, because UAs impersonate each other. */
const BROWSERS: readonly Matcher[] = [
  { pattern: /\bEdg(?:e|A|iOS)?\/[\d.]+/i, label: 'Edge' },
  { pattern: /\bOPR\/[\d.]+|\bOpera\b/i, label: 'Opera' },
  { pattern: /\bSamsungBrowser\/[\d.]+/i, label: 'Samsung Internet' },
  { pattern: /\bBrave\b/i, label: 'Brave' },
  { pattern: /\bVivaldi\/[\d.]+/i, label: 'Vivaldi' },
  { pattern: /\bDuckDuckGo\/[\d.]+/i, label: 'DuckDuckGo' },
  { pattern: /\bHeadlessChrome\/[\d.]+/i, label: 'Headless Chrome' },
  { pattern: /\bCriOS\/[\d.]+/i, label: 'Chrome' },
  { pattern: /\bFxiOS\/[\d.]+/i, label: 'Firefox' },
  { pattern: /\bChrom(?:e|ium)\/[\d.]+/i, label: 'Chrome' },
  { pattern: /\bFirefox\/[\d.]+/i, label: 'Firefox' },
  { pattern: /\bVersion\/[\d.]+.*\bSafari\/[\d.]+/i, label: 'Safari' },
  { pattern: /\bSafari\/[\d.]+/i, label: 'Safari' },
  { pattern: /\bMSIE\b|\bTrident\//i, label: 'Internet Explorer' },
];

/** Platform matchers — again most specific first (iPad claims to be a Mac). */
const PLATFORMS: readonly Matcher[] = [
  { pattern: /\biPhone\b/i, label: 'iPhone' },
  { pattern: /\biPad\b/i, label: 'iPad' },
  { pattern: /\biPod\b/i, label: 'iPod touch' },
  { pattern: /\bAndroid\b/i, label: 'Android' },
  { pattern: /\bCrOS\b/i, label: 'ChromeOS' },
  { pattern: /\bWindows Phone\b/i, label: 'Windows Phone' },
  { pattern: /\bWindows\b/i, label: 'Windows' },
  { pattern: /\bMac OS X\b|\bMacintosh\b/i, label: 'macOS' },
  { pattern: /\bLinux\b|\bX11\b|\bUbuntu\b|\bFedora\b/i, label: 'Linux' },
];

/** Requests that are plainly not a browser: SDKs, CLIs, monitors. */
const NON_BROWSERS: readonly Matcher[] = [
  { pattern: /^curl\//i, label: 'curl' },
  { pattern: /^Wget\//i, label: 'Wget' },
  { pattern: /^PostmanRuntime\//i, label: 'Postman' },
  { pattern: /^node(?:-fetch)?\/|\bundici\b/i, label: 'Node.js client' },
  { pattern: /^python-requests\/|^python-urllib/i, label: 'Python client' },
  { pattern: /^okhttp\//i, label: 'Android app' },
  { pattern: /\bCFNetwork\//i, label: 'Apple app' },
];

function firstMatch(matchers: readonly Matcher[], userAgent: string): string | null {
  for (const matcher of matchers) {
    if (matcher.pattern.test(userAgent)) return matcher.label;
  }
  return null;
}

/**
 * "Chrome on Linux", "Safari on iPhone", "Firefox", "Android" — or
 * `UNKNOWN_DEVICE` when the string carries nothing recognisable. Never
 * returns the raw user agent.
 */
export function describeDevice(userAgent: string | null | undefined): string {
  const ua = (userAgent ?? '').trim();
  if (ua === '') return UNKNOWN_DEVICE;

  const tool = firstMatch(NON_BROWSERS, ua);
  if (tool !== null) return tool;

  const browser = firstMatch(BROWSERS, ua);
  const platform = firstMatch(PLATFORMS, ua);
  if (browser !== null && platform !== null) return `${browser} on ${platform}`;
  if (browser !== null) return browser;
  if (platform !== null) return platform;
  return UNKNOWN_DEVICE;
}

/**
 * The label to store / show for a session: an explicit client-supplied device
 * name wins (it is the user's own words), otherwise the parsed user agent.
 * Guarantees a non-empty, human-readable string.
 */
export function deviceLabel(
  clientDeviceName: string | null | undefined,
  userAgent: string | null | undefined
): string {
  const explicit = (clientDeviceName ?? '').trim();
  if (explicit !== '' && !looksLikeUserAgent(explicit)) return explicit;
  return describeDevice(userAgent ?? explicit);
}

/** A stored `device` that is really a raw UA (legacy rows) should be re-described. */
export function looksLikeUserAgent(value: string): boolean {
  return /^Mozilla\/\d|\bAppleWebKit\/|\bGecko\/|\bChrome\/\d|\bSafari\/\d/.test(value);
}
